/**
 * Persisted cart countdown for the optional cart-drawer countdown block.
 * The timer stores an absolute expiry so section re-renders never reset it.
 */
(function initializeCartCountdown(global) {
  const memoryExpiries = new Map();
  const defaultStorageKey = 'theme-cart-countdown-expiry';

  function normalizeDuration(value) {
    const duration = Number.parseInt(value, 10);
    return Number.isFinite(duration) ? Math.max(0, duration) : 0;
  }

  function readStoredExpiry(storage, key) {
    try {
      const value = Number.parseInt(storage?.getItem(key), 10);
      return Number.isFinite(value) && value > 0 ? value : null;
    } catch (_) {
      return null;
    }
  }

  function readOrCreateExpiry({
    durationSeconds,
    key = defaultStorageKey,
    now = Date.now(),
    storage,
    memory = memoryExpiries,
  }) {
    const storedExpiry = readStoredExpiry(storage, key);
    if (storedExpiry !== null) {
      memory.set(key, storedExpiry);
      return storedExpiry;
    }

    const memoryExpiry = memory.get(key);
    if (Number.isFinite(memoryExpiry)) return memoryExpiry;

    const expiry = now + normalizeDuration(durationSeconds) * 1000;
    memory.set(key, expiry);

    try {
      storage?.setItem(key, String(expiry));
    } catch (_) {
      // sessionStorage can be unavailable; the page-memory fallback still
      // prevents resets during cart drawer re-renders in this page life.
    }

    return expiry;
  }

  function getRemainingSeconds(expiry, now = Date.now()) {
    if (!Number.isFinite(expiry)) return 0;
    return Math.max(0, Math.ceil((expiry - now) / 1000));
  }

  function clearExpiry({ key = defaultStorageKey, storage, memory = memoryExpiries }) {
    memory.delete(key);
    try {
      storage?.removeItem(key);
    } catch (_) {
      // Clearing page memory is sufficient when storage is unavailable.
    }
  }

  function clearCountdownWhenCartIsEmpty(
    cartData,
    { key = defaultStorageKey, storage, memory = memoryExpiries } = {}
  ) {
    const cartIsEmpty =
      cartData?.item_count === 0 ||
      (Array.isArray(cartData?.items) && cartData.items.length === 0);

    if (!cartIsEmpty) return false;
    clearExpiry({ key, storage, memory });
    return true;
  }

  const api = {
    clearCountdownWhenCartIsEmpty,
    getRemainingSeconds,
    readOrCreateExpiry,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!global?.customElements || !global.HTMLElement) return;

  class CountdownTimer extends global.HTMLElement {
    connectedCallback() {
      this._storageKey = this.dataset.storageKey || defaultStorageKey;
      try {
        this._storage = global.sessionStorage;
      } catch (_) {
        this._storage = null;
      }
      if (this.dataset.cartEmpty === 'true') {
        clearCountdownWhenCartIsEmpty(
          { item_count: 0 },
          { key: this._storageKey, storage: this._storage }
        );
        this._expiry = Date.now();
      } else {
        this._expiry = readOrCreateExpiry({
          durationSeconds: this.dataset.duration,
          key: this._storageKey,
          storage: this._storage,
        });
      }

      this._render();
      if (getRemainingSeconds(this._expiry) > 0) {
        this._interval = global.setInterval(() => this._tick(), 1000);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            clearCountdownWhenCartIsEmpty(event?.cartData, {
              key: this._storageKey,
              storage: this._storage,
            })
          ) {
            this._expiry = Date.now();
            this._stop();
            this._render();
          }
        });
      }
    }

    disconnectedCallback() {
      this._stop();
      this._unsubscribe?.();
      this._unsubscribe = null;
    }

    _tick() {
      this._render();
      if (getRemainingSeconds(this._expiry) === 0) this._stop();
    }

    _stop() {
      if (this._interval) global.clearInterval(this._interval);
      this._interval = null;
    }

    _render() {
      const remaining = getRemainingSeconds(this._expiry);
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      this.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
  }

  if (!global.customElements.get('countdown-timer')) {
    global.customElements.define('countdown-timer', CountdownTimer);
  }
})(typeof window !== 'undefined' ? window : globalThis);
